import { _ } from '@icc/shared/helpers';
import { SalesDocumentType } from './sales-document-types';
import { DocumentFlag } from './models/document-flag.model';

export const enum DocumentBuiltinFlag {
    isConfirmed = 'isConfirmed',
    isSentToBuyer = 'isSentToBuyer',
    isOrderCreated = 'isOrderCreated',
    isOrderPlaced = 'isOrderPlaced',
    isCanceled = 'isCanceled',
    isOpened = 'isOpened',
    isAcceptedForProcessing = 'isAcceptedForProcessing',
    isInProgress = 'isInProgress',
    isCompleted = 'isCompleted',
    isAnnexed = 'isAnnexed',
    isAdvancePaid = 'isAdvancePaid',
    isTotalPaid = 'isTotalPaid',
    isDelivered = 'isDelivered',
    isEmpty = 'isEmpty',
}

export const DocumentBuiltinFlags = {
    [SalesDocumentType.Quotation]: [
        DocumentBuiltinFlag.isEmpty,
        DocumentBuiltinFlag.isConfirmed,
        DocumentBuiltinFlag.isSentToBuyer,
        DocumentBuiltinFlag.isOrderCreated,
        DocumentBuiltinFlag.isOrderPlaced,
        DocumentBuiltinFlag.isCanceled,
    ],
    [SalesDocumentType.Inquiry]: [
        DocumentBuiltinFlag.isOpened,
        DocumentBuiltinFlag.isConfirmed,
        DocumentBuiltinFlag.isCanceled,
    ],
    [SalesDocumentType.Order]: [
        DocumentBuiltinFlag.isConfirmed,
        DocumentBuiltinFlag.isOrderPlaced,
        DocumentBuiltinFlag.isOpened,
        DocumentBuiltinFlag.isAcceptedForProcessing,
        DocumentBuiltinFlag.isAdvancePaid,
        DocumentBuiltinFlag.isTotalPaid,
        DocumentBuiltinFlag.isInProgress,
        DocumentBuiltinFlag.isDelivered,
        DocumentBuiltinFlag.isCompleted,
        DocumentBuiltinFlag.isAnnexed,
        DocumentBuiltinFlag.isCanceled,
    ],
    [SalesDocumentType.ServiceOrder]: [
        DocumentBuiltinFlag.isOrderPlaced,
        DocumentBuiltinFlag.isDelivered,
        DocumentBuiltinFlag.isCanceled,
    ],
};
export const DocumentBuiltinFlagDict = {
    [DocumentBuiltinFlag.isConfirmed]: _('SALES_DOCUMENT_FLAG|Potwierdzony'),
    [DocumentBuiltinFlag.isSentToBuyer]: _('SALES_DOCUMENT_FLAG|Wysłany do kupującego'),
    [DocumentBuiltinFlag.isOrderCreated]: _('SALES_DOCUMENT_FLAG|Przygotowany do zamówienia'),
    [DocumentBuiltinFlag.isOrderPlaced]: _('SALES_DOCUMENT_FLAG|Zamówiony'),
    [DocumentBuiltinFlag.isCanceled]: _('SALES_DOCUMENT_FLAG|Anulowany'),
    [DocumentBuiltinFlag.isOpened]: _('SALES_DOCUMENT_FLAG|Otwarty'),
    [DocumentBuiltinFlag.isAcceptedForProcessing]: _('SALES_DOCUMENT_FLAG|Przyjęty do realizacji'),
    [DocumentBuiltinFlag.isInProgress]: _('SALES_DOCUMENT_FLAG|W trakcie realizacji'),
    [DocumentBuiltinFlag.isCompleted]: _('SALES_DOCUMENT_FLAG|Zrealizowany'),
    [DocumentBuiltinFlag.isAnnexed]: _('SALES_DOCUMENT_FLAG|Aneksowany'),
    [DocumentBuiltinFlag.isAdvancePaid]: _('SALES_DOCUMENT_FLAG|Zaliczka została opłacona'),
    [DocumentBuiltinFlag.isTotalPaid]: _('SALES_DOCUMENT_FLAG|Opłacony w całości'),
    [DocumentBuiltinFlag.isDelivered]: _('SALES_DOCUMENT_FLAG|Dostarczony'),
    [DocumentBuiltinFlag.isEmpty]: _('SALES_DOCUMENT_FLAG|Pusty'),
};

export const DocumentBuiltinFlagsSettings: Record<
    DocumentBuiltinFlag,
    Pick<
        DocumentFlag,
        | 'buyerSideVisibility'
        | 'canBuyerActivate'
        | 'canBuyerDeactivate'
        | 'canSellerActivate'
        | 'canSellerDeactivate'
        | 'resetAfterOrderAnnexation'
        | 'resetAfterQuotationCopying'
        | 'resetAfterQuotationModification'
    >
> = {
    [DocumentBuiltinFlag.isConfirmed]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isSentToBuyer]: {
        buyerSideVisibility: false,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isOrderCreated]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: false,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isOrderPlaced]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isCanceled]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: false,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isOpened]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isAcceptedForProcessing]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: true,
        canSellerDeactivate: true,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isInProgress]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: true,
        canSellerDeactivate: true,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isCompleted]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: true,
        canSellerDeactivate: true,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isAnnexed]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isAdvancePaid]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: true,
        canSellerDeactivate: true,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isTotalPaid]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: true,
        canSellerDeactivate: true,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isDelivered]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: true,
        canSellerDeactivate: true,
        resetAfterOrderAnnexation: true,
        resetAfterQuotationCopying: true,
        resetAfterQuotationModification: true,
    },
    [DocumentBuiltinFlag.isEmpty]: {
        buyerSideVisibility: true,
        canBuyerActivate: false,
        canBuyerDeactivate: false,
        canSellerActivate: false,
        canSellerDeactivate: false,
        resetAfterOrderAnnexation: false,
        resetAfterQuotationCopying: false,
        resetAfterQuotationModification: false,
    },
};
